import clsx from 'clsx';
import { useEffect } from 'react';
import styles from './material-symbol-link.module.css';
/**
 * This is the link to the Material Symbols font.
 * It is used to load the font and the icons.
 * The icons are loaded from the font and are named
 * using the Material Symbols font naming convention.
 *
 * It is important that we regulate the extent of icons that
 * get loaded on our side. The full bundle is 4mb, and we only
 * need a tiny portion of it:
 *
 * As of today:
 * - we only use the FILL property (0/1)
 * - the grade (GRAD) property is always 0
 * - the optical size (OPSZ) property is always 24
 *
 * @see https://fonts.google.com/icons
 */

const weights = [300, 600] as const;
type Weight = (typeof weights)[number];
const weightOptions = weights.map((weight) => `24,${weight},0..1,0`).join(';');
const MATERIAL_SYMBOLS_LINK_WITH_OPTIONS = `https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@${weightOptions}`;

// Add icons here that you need to use in the project.
const icons = [
  'account_circle',
  'add',
  'arrow_back',
  'arrow_back_ios',
  'arrow_forward',
  'attach_file',
  'calendar_today',
  'call',
  'chat',
  'check',
  'chevron_left',
  'chevron_right',
  'close',
  'confirmation_number',
  'content_copy',
  'dangerous',
  'description',
  'distance',
  'download',
  'edit',
  'error',
  'filter_list',
  'help',
  'home',
  'home_health',
  'info',
  'image',
  'keyboard_arrow_down',
  'keyboard_arrow_up',
  'keyboard_double_arrow_right',
  'local_shipping',
  'location_on',
  'lock',
  'mail',
  'menu',
  'near_me',
  'pill',
  'pin_drop',
  'remove',
  'school',
  'search',
  'send',
  'swap_vert',
  'sync',
  'visibility',
  'visibility_off',
  'verified_user',
  'warning',
] as const;

export type IconName = (typeof icons)[number];

export function useMaterialIconsStylesheet() {
  useEffect(() => {
    const linkUrl = new URL(MATERIAL_SYMBOLS_LINK_WITH_OPTIONS);

    const iconNames = [...icons].sort().join(',');

    linkUrl.searchParams.set('icon_names', iconNames);

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = linkUrl.toString();
    document.head.appendChild(link);
  }, []);
}

interface MaterialSymbolIconBaseProps {
  name: IconName;
  fill?: boolean;
  className?: string;
  weight?: Weight;
}

interface MaterialSymbolIconButtonProps extends MaterialSymbolIconBaseProps {
  onClick: () => void;
  ariaLabel: string;
  disabled?: boolean;
}

interface MaterialSymbolIconSpanProps extends MaterialSymbolIconBaseProps {
  onClick?: never;
  ariaLabel?: never;
  disabled?: never;
}

export type MaterialSymbolIconProps = MaterialSymbolIconButtonProps | MaterialSymbolIconSpanProps;

export function MaterialSymbolIcon({
  name,
  fill = false,
  className,
  weight = 300,
  ...buttonProps
}: MaterialSymbolIconProps) {
  const fontVariationSettings = `'FILL' ${fill ? 1 : 0}, 'wght' ${weight}, 'GRAD' 0, 'opsz' 24`;

  const commonProps = {
    className: clsx('material-symbols-outlined', styles.symbol, className),
    style: { fontVariationSettings },
  };

  if (buttonProps?.onClick) {
    return (
      <button
        type="button"
        {...commonProps}
        onClick={buttonProps.onClick}
        aria-label={buttonProps.ariaLabel}
        disabled={buttonProps.disabled}
      >
        {name}
      </button>
    );
  }

  return <span {...commonProps}>{name}</span>;
}
